import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import * as Fonts from "../../components/Fonts";
import { KeepoLoader } from "../../components/KeepoLoader";
import { PlusTag } from "../../components/PlusTag";
import { api, hasActiveSubscription } from "../../services/api";
import { isApiError } from "../../services/api/errors";
import {
  DOMAIN_SEARCH_ERRORS,
  DomainResult,
  DomainSearchResult,
  cyclesOf,
  FROM_PRICE,
  getDomainExtensionsTotal,
  getMyDomains,
  OwnedDomain,
  searchDomains,
} from "../../services/domains";
import { formatMoney } from "../../utils/money";
import { Benefits, Hero } from "../Subscription/pages/Features/styles";
import { CycleOptions } from "./CycleOptions";
import { RecommendationReason, recommendationsFor } from "./recommend";
// The same hero, layout and benefits grid as the PLUS page
import {
  BuyButton,
  ExactResult,
  MobileLinks,
  Page,
  Price,
  Results,
  SearchCard,
  SearchContent,
  SearchForm,
  SmallButton,
  Suggestions,
  Tag,
} from "./styles";

// A few of the extensions sold, under the search (just to show what there is, not clickable)
const QUICK_TLDS = ["com", "net", "org", "store", "online", "site", "shop"];

// Why register with Keepo: two per row, as on the PLUS page
const BENEFITS = [
  { key: "privacy", icon: "🛡️" },
  { key: "email", icon: "✉️" },
  { key: "keepo", icon: "⚡" },
  { key: "ssl", icon: "🔒" },
  { key: "payment", icon: "💳" },
  { key: "dns", icon: "⚙️" },
];

/**
 * Domains: search and buy a domain of your own (sold through Namecheap, with Keepo's margin). The person's
 * domains are in /domains/mine (a subitem in the sidebar).
 */
// "Apenas R$ X por 5 anos": only when 5 years come to at most this a year (R$)
const FIVE_YEARS_MAX_PER_YEAR = 100;

const DomainsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const prefix = "pages.domains";
  // The search lives in the URL (?domain=seunome.com): shareable, and a link can open it already searched
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("domain") || "");
  // Came from a Keepo (its custom domain screen): the domain bought here is connected to it
  const site = searchParams.get("site");
  const [isSearching, setSearching] = useState(false);
  const [results, setResults] = useState<DomainSearchResult>();
  // The cycle picked for the name searched for (1, 2 or 5 years)
  const [years, setYears] = useState(1);
  const [searchError, setSearchError] = useState<keyof typeof DOMAIN_SEARCH_ERRORS>();
  const [domains, setDomains] = useState<OwnedDomain[]>([]);
  // Extensions that suit the person's Keepos (their field of work), and why
  const [recommended, setRecommended] = useState<Map<string, RecommendationReason>>(new Map());

  useEffect(() => {
    api
      .getSites()
      .then(sites =>
        setRecommended(
          recommendationsFor(
            sites.map(item => ({
              category: (item.site || item.draft)?.category,
              plus: hasActiveSubscription(item.subscription),
            }))
          )
        )
      )
      .catch(() => undefined);
  }, []);
  // How many extensions are on sale: the ones not shown under the search become "+N"
  const [extensionsTotal, setExtensionsTotal] = useState<number>();

  // The phones' link to "My domains" (with how many) and the extensions' "+N"
  useEffect(() => {
    getMyDomains()
      .then(setDomains)
      .catch(() => undefined);
    getDomainExtensionsTotal()
      .then(setExtensionsTotal)
      .catch(() => undefined);
    // Opened with ?domain=: searched right away
    if (query.trim()) {
      search();
    }
    // Once, on arriving
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The prices are in reais (Keepo charges in BRL)
  const formatBRL = (value: number) => formatMoney(Math.round(value * 100), "brl", i18n.language);

  const search = async (value = query) => {
    if (!value.trim()) {
      return;
    }
    setSearchParams({ domain: value.trim(), ...(site ? { site } : {}) }, { replace: true });
    setSearching(true);
    setSearchError(undefined);
    try {
      setResults(await searchDomains(value));
    } catch (error) {
      setResults(undefined);
      setSearchError(
        isApiError(error, DOMAIN_SEARCH_ERRORS.invalid)
          ? "invalid"
          : isApiError(error, DOMAIN_SEARCH_ERRORS.rateLimit)
            ? "rateLimit"
            : "unavailable"
      );
    } finally {
      setSearching(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    search();
  };

  const buy = (domain: string, cycle = 1) =>
    navigate(
      `/domains/checkout?domain=${encodeURIComponent(domain)}&years=${cycle}${site ? `&site=${encodeURIComponent(site)}` : ""}`
    );

  // The other options: the year, with "for 1 year" under it (and the renewal when it costs more, so it's no surprise)
  // "R$ 109,90/ano" on one line (the renewal is shown at checkout)
  const price = (result: DomainResult) =>
    result.price && (
      <Price>
        <b>{formatBRL(result.price.register)}</b>
        <small>{t(`${prefix}.price.slashYear`)}</small>
      </Price>
    );

  // What's worth calling out about a domain: only the most important one
  const tagsOf = (result: DomainResult) => {
    const tags: { key: string; label: string; tone: "plus" | "warning" | "neutral" }[] = [];
    const p = result.price;
    if (result.available && p) {
      // For the person's field of work, saying why (e.g. "Ideal pra sua loja")
      const reason = recommended.get(result.tld);
      if (reason) {
        tags.push({ key: "reason", label: t(`${prefix}.tags.reasons.${reason}`), tone: "plus" });
      }
      // Cheaper than buying straight from Namecheap (its retail in BRL, with the IOF): "X% OFF"
      const off = p.retail ? Math.round((1 - p.register / p.retail) * 100) : 0;
      if (off >= 1) {
        tags.push({ key: "off", label: t(`${prefix}.tags.off`, { percent: off }), tone: "plus" });
      }
      // Cheap for the long run: 5 years at up to FIVE_YEARS_MAX_PER_YEAR a year (a cheap first year often
      // renews high, so it's the 5-year price that counts)
      const fiveYears = p.cycles?.find(cycle => cycle.years === 5);
      if (fiveYears && fiveYears.price / 5 <= FIVE_YEARS_MAX_PER_YEAR) {
        tags.push({
          key: "fiveYears",
          label: t(`${prefix}.tags.fiveYears`, { value: formatBRL(fiveYears.price) }),
          tone: "plus",
        });
      }
      if (result.tld === "com") {
        tags.push({ key: "popular", label: t(`${prefix}.tags.popular`), tone: "neutral" });
      }
    }
    if (!result.available && result.premium) {
      tags.push({ key: "premium", label: t(`${prefix}.tags.premium`), tone: "warning" });
    }
    return tags.slice(0, 1).map(tag => (
      <Tag key={tag.key} $tone={tag.tone} className="seal">
        {tag.label}
      </Tag>
    ));
  };

  return (
    <Page>
      <SearchContent>
        <MobileLinks>
          <SmallButton type="button" onClick={() => navigate("/domains/mine")}>
            {domains.length ? t(`${prefix}.nav.mineCount`, { count: domains.length }) : t(`${prefix}.nav.mine`)} →
          </SmallButton>
        </MobileLinks>

        <Hero>
          <PlusTag>{t(`${prefix}.hero.tag`)}</PlusTag>
          <h1>{t(`${prefix}.hero.title`)}</h1>
          <p className="subtitle">{t(`${prefix}.hero.subtitle`, { value: formatBRL(FROM_PRICE) })}</p>

          <SearchCard>
            <SearchForm onSubmit={submit} role="search">
              <label className="field">
                <span className="prefix">www.</span>
                <input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder={t(`${prefix}.search.placeholder`)}
                  aria-label={t(`${prefix}.search.label`)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </label>
              <button type="submit" disabled={isSearching || !query.trim()} aria-busy={isSearching}>
                {isSearching ? (
                  <KeepoLoader size={13} color="var(--color-neutral-white)" />
                ) : (
                  t(`${prefix}.search.button`)
                )}
              </button>
            </SearchForm>
            <div className="tlds">
              {QUICK_TLDS.map(tld => (
                <span key={tld} className="tld">
                  .{tld}
                </span>
              ))}
              {!!extensionsTotal && extensionsTotal > QUICK_TLDS.length && (
                <span className="tld">+{extensionsTotal - QUICK_TLDS.length}</span>
              )}
              <span className="soon">{t(`${prefix}.search.comBrSoon`)}</span>
            </div>
          </SearchCard>

          {searchError && (
            <p className="error" role="alert">
              {t(`${prefix}.search.errors.${searchError}`)}
            </p>
          )}
        </Hero>

        {results && (
          <Results aria-live="polite">
            <Fonts.Label>{t(`${prefix}.results.title`)}</Fonts.Label>
            <ExactResult $available={results.exact.available}>
              <div className="top">
                <span className="name">
                  <span className="title">
                    <span className="domain">{results.exact.domain}</span>
                    {tagsOf(results.exact)}
                  </span>
                  <span className="status">
                    {results.exact.available
                      ? t(`${prefix}.results.available`)
                      : results.exact.premium
                        ? t(`${prefix}.results.premium`)
                        : results.exact.notSold
                          ? t(`${prefix}.results.notSold`, { tld: results.exact.tld })
                          : t(`${prefix}.results.taken`)}
                  </span>
                </span>
                {results.exact.available && (
                  <BuyButton type="button" onClick={() => buy(results.exact.domain, years)}>
                    {t(`${prefix}.results.buy`)}
                  </BuyButton>
                )}
              </div>
              {results.exact.available && results.exact.price && (
                <>
                  <CycleOptions
                    cycles={cyclesOf(results.exact.price)}
                    selected={years}
                    onSelect={setYears}
                    formatBRL={formatBRL}
                  />
                </>
              )}
            </ExactResult>

            <Fonts.Label className="suggestions">{t(`${prefix}.results.suggestions`)}</Fonts.Label>
            <Suggestions>
              {/* The ones that suit the person's Keepos first (the rest keep their order) */}
              {[...results.suggestions]
                .sort(
                  (a, b) =>
                    Number(b.available && recommended.has(b.tld)) - Number(a.available && recommended.has(a.tld))
                )
                .map(result => (
                  <li key={result.domain} className={result.available ? undefined : "taken"}>
                    <span className="domain">
                      {/* The name and the extension apart: a long name is cut, the extension always shows */}
                      <span className="name">
                        <span>{result.domain.slice(0, -(result.tld.length + 1))}</span>
                        <span className="tld">.{result.tld}</span>
                      </span>
                      {result.available && tagsOf(result)}
                    </span>
                    <span className="right">
                      {result.available ? (
                        <>
                          {price(result)}
                          <BuyButton type="button" className="secondary" onClick={() => buy(result.domain)}>
                            {t(`${prefix}.results.buy`)}
                          </BuyButton>
                        </>
                      ) : (
                        <Tag>{t(`${prefix}.results.${result.premium ? "premiumShort" : "taken"}`)}</Tag>
                      )}
                    </span>
                  </li>
                ))}
            </Suggestions>
          </Results>
        )}

        <Benefits>
          <h2>{t(`${prefix}.benefits.title`)}</h2>
          <ul>
            {BENEFITS.map(({ key, icon }) => (
              <li key={key}>
                <span className="icon" aria-hidden="true">
                  {icon}
                </span>
                <span className="text">
                  <b>{t(`${prefix}.benefits.${key}.title`)}</b>
                  <span>{t(`${prefix}.benefits.${key}.text`)}</span>
                </span>
              </li>
            ))}
          </ul>
        </Benefits>
      </SearchContent>
    </Page>
  );
};

export default DomainsPage;
